import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";
import Tooltip from "@ats/src/components/shared/Tooltip";
import { INTERNAL_UTM_PARAMS } from "@shared/lib/utils";

interface PurchaseAiCreditTopUpConfirmModalProps {
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  credits: number;
  amountDueToday: string;
  paymentMethodLabel: string;
}

function PurchaseAiCreditTopUpConfirmModal({
  onCancel,
  onConfirm,
  isLoading = false,
  credits,
  amountDueToday,
  paymentMethodLabel,
}: PurchaseAiCreditTopUpConfirmModalProps) {
  return (
    <CenterModal headerTitleText="Confirm your purchase" onCancel={onCancel} hasUnsavedChanges={false}>
      <Styled.Credits>{credits.toLocaleString()} credits</Styled.Credits>
      <Styled.Subtext>One-time top-up, added to your balance.</Styled.Subtext>

      <Styled.Divider />

      <Styled.DueRow>
        <span>Amount due today</span>
        <span className="num">{amountDueToday}</span>
      </Styled.DueRow>

      <Styled.MethodRow>
        <span>Payment method</span>
        <Styled.Card>
          {paymentMethodLabel}
          <Tooltip label="To add a new payment method, go to Manage Billing.">
            <Icon name="info" />
          </Tooltip>
        </Styled.Card>
      </Styled.MethodRow>

      <Styled.Terms>
        By confirming, you agree to Polymer&rsquo;s <a href={`https://polymer.co/terms${INTERNAL_UTM_PARAMS}`} target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
        <a href={`https://polymer.co/privacy${INTERNAL_UTM_PARAMS}`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </Styled.Terms>

      <Styled.ButtonContainer>
        <Button onClick={onConfirm} loading={isLoading}>
          Confirm
        </Button>
        <Button styleType="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </Styled.ButtonContainer>
    </CenterModal>
  );
}

export default PurchaseAiCreditTopUpConfirmModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Credits = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_Credits;
    ${[t.text.md, t.text.bold]}
    color: ${t.dark ? t.color.gray[100] : t.color.black};
  `;
});

Styled.Subtext = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_Subtext;
    ${[t.text.sm, t.mt("px")]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Divider = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_Divider;
    height: 1px;
    ${[t.my(4)]}
    background: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
  `;
});

Styled.DueRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_DueRow;
    ${[t.text.md, t.text.bold]}
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    color: ${t.dark ? t.color.gray[100] : t.color.black};

    .num {
      font-variant-numeric: tabular-nums;
    }
  `;
});

Styled.MethodRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_MethodRow;
    ${[t.text.sm, t.mt(2)]}
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Card = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_Card;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    svg {
      width: 13px;
      height: 13px;
    }
  `;
});

Styled.Terms = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_Terms;
    ${[t.text.xs, t.mt(5)]}
    line-height: 1.5;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    a {
      color: ${t.dark ? t.color.gray[200] : t.color.black};
      text-decoration: underline;
    }
  `;
});

Styled.ButtonContainer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: PurchaseAiCreditTopUpConfirmModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}
      &:last-of-type {
        ${t.mr(0)}
      }
    }
    ${t.mt(6)}
  `;
});
